import { z } from 'zod';
import { canonicalize } from '../hashing/canonical.js';
import { digestSchema } from '../hashing/hash.js';
import { templateIdSchema } from './templates.js';
/**
 * A Communication: the record of one email, as it was actually sent (#61).
 *
 * The body is the RENDERED bytes — subject, HTML and text exactly as the transport accepted
 * them — never a template id plus a context that would re-render differently after a copy
 * edit. That is the mistake `pinnedDefinitionSchema` already avoids, and it is what lets a
 * surveyor be shown what a person received rather than what the template says today.
 *
 * Strict, and with no field for anything that must never be here: no attachments, no
 * CC/BCC, no reply, no template context, no token value, no access code. `linkedTokenIds`
 * holds token DOCUMENT ids — the hash a token is stored under — so a raw token or a code
 * cannot fit the shape.
 */
/**
 * `sending` is the in-flight mark: set immediately before the provider is asked, cleared by
 * the answer. A record found `sending` by a later delivery is AMBIGUOUS — the provider may
 * have accepted the message — and is parked for an operator, never resent (docs/mvp/pilot-runbook.md).
 */
export const communicationStatuses = ['queued', 'sending', 'sent', 'failed', 'parked'];
export const communicationStatusSchema = z.enum(communicationStatuses);
export const communicationSchema = z
    .object({
    /** `comm:<key>` — deterministic, so a redelivery meets its own record. */
    id: z.string().min(1),
    personId: z.string().min(1),
    docType: z.literal('communication').default('communication'),
    templateId: templateIdSchema.nullable(),
    subject: z.string().min(1),
    html: z.string().min(1),
    text: z.string().min(1),
    /** The Entra object id of the HR user whose action caused this, or `system`. */
    sentBy: z.string().min(1),
    queuedAt: z.string().datetime(),
    sentAt: z.string().datetime().nullable().default(null),
    status: communicationStatusSchema,
    providerMessageId: z.string().min(1).nullable().default(null),
    linkedInstanceIds: z.array(z.string().min(1)).default([]),
    /** Token document ids only — a stored token's hash, never its value. */
    linkedTokenIds: z.array(digestSchema).default([]),
    /** Bounded and sanitised; the provider's words, never the message's. */
    failureReason: z.string().max(200).nullable().default(null),
    /** Why an operator has to look: set with `parked`, never from the message. */
    parkedReason: z.string().max(200).nullable().default(null),
    /** SHA-256 of the exact MailMessage handed to the transport — the attestation of what was sent. */
    contentHash: digestSchema,
    /** True when a bearer secret was redacted from the stored snapshot. */
    redacted: z.boolean(),
})
    .strict();
export function communicationId(key) {
    return `comm:${key}`;
}
/** What identifies the message for ever — everything a delivery transition may not touch. */
export function communicationSnapshot(record) {
    return canonicalize({
        id: record.id,
        personId: record.personId,
        templateId: record.templateId,
        subject: record.subject,
        html: record.html,
        text: record.text,
        sentBy: record.sentBy,
        queuedAt: record.queuedAt,
        linkedInstanceIds: [...record.linkedInstanceIds],
        linkedTokenIds: [...record.linkedTokenIds],
        contentHash: record.contentHash,
        redacted: record.redacted,
    });
}
/** `message` is the SANITISED message; `attestation.contentHash` is of the delivered one. */
export function queuedCommunication(message, origin, queuedAt, attestation) {
    return communicationSchema.parse({
        id: communicationId(origin.key),
        personId: origin.personId,
        templateId: message.templateId,
        subject: message.subject,
        html: message.html,
        text: message.text,
        sentBy: origin.sentBy,
        queuedAt,
        status: 'queued',
        linkedInstanceIds: [...origin.linkedInstanceIds],
        linkedTokenIds: [...origin.linkedTokenIds],
        contentHash: attestation.contentHash,
        redacted: attestation.redacted,
    });
}
/**
 * The only changes a Communication ever undergoes: `queued → sending → sent | failed | parked`
 * (a direct `queued → sent` is still legal for a sender that never marks in-flight). A sent record is
 * final: it cannot be downgraded, failed, or sent again. The snapshot fields are copied
 * through untouched, so a transition cannot rewrite what was sent even by accident.
 */
export function applyCommunicationTransition(current, transition) {
    const open = current.status === 'queued' || current.status === 'sending';
    if (!open || (transition.kind === 'sending' && current.status !== 'queued')) {
        throw new Error(`communication ${current.id} is ${current.status}; it cannot become ${transition.kind}`);
    }
    switch (transition.kind) {
        case 'sending':
            return communicationSchema.parse({ ...current, status: 'sending' });
        case 'sent':
            return communicationSchema.parse({ ...current, status: 'sent', sentAt: transition.sentAt, providerMessageId: transition.providerMessageId });
        case 'failed':
            return communicationSchema.parse({ ...current, status: 'failed', failureReason: safeFailureReason(transition.failureReason) });
        case 'parked':
            return communicationSchema.parse({ ...current, status: 'parked', parkedReason: transition.reason === undefined ? current.parkedReason : safeOperationalReason(transition.reason) });
    }
}
/** One line, at most 200 characters, and nothing from the message itself is ever passed here. */
export function safeFailureReason(reason) {
    return reason.replace(/[\r\n\t]+/g, ' ').trim().slice(0, 200) || 'send failed';
}
/**
 * The operator-facing reason may originate outside this code path, so redact risky shapes
 * again at the response boundary. Keep it useful, but never let a link, token, filename,
 * access code, message body or stack frame become a second disclosure surface.
 */
export function safeOperationalReason(reason) {
    if (reason === null || reason === undefined)
        return null;
    const oneLine = safeFailureReason(reason);
    const redacted = oneLine
        .replace(/<[^>]+>[^<]{0,200}<\/[^>]+>/gi, '[message content removed]')
        .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, '[token removed]')
        .replace(/\beyJ[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\.[A-Za-z0-9._-]+\b/g, '[token removed]')
        .replace(/\bhttps?:\/\/\S+/gi, '[link removed]')
        .replace(/\b(?:access\s*)?code\s*[:#-]?\s*[A-Z0-9-]{4,16}\b/gi, '[code removed]')
        .replace(/\b[^\s/\\]+\.(?:csv|doc|docx|heic|html?|jpe?g|pdf|png|txt|xls|xlsx)\b/gi, '[filename removed]')
        .replace(/\bat\s+[\w$.<>]+\s*\([^)]*\)/g, '[stack frame removed]');
    return redacted.replace(/\s+/g, ' ').trim().slice(0, 200) || 'operator review needed';
}
//# sourceMappingURL=communication.js.map